import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useCallback } from 'react';

import { Sport } from '#src/__generated__/globalTypes.ts';

import type {
  CurrentUserAcceptedCurrenciesQuery,
  CurrentUserAcceptedCurrenciesQueryVariables,
} from './__generated__/useCurrentUserAcceptedCurrencies.graphql.ts';
import { useQuery } from './graphql/useQuery.ts';
import type { AcceptedCurrenciesValue } from './useAcceptedCurrencies.ts';
import {
  MARKETPLACE_PREFERENCES_ACCEPTED_CURRENCIES,
  useAcceptedCurrencies,
} from './useAcceptedCurrencies.ts';
import { useUpdateUserProfile } from './useUpdateUserProfile.ts';

const CURRENT_USER_ACCEPTED_CURRENCIES_QUERY = gql`
  query CurrentUserAcceptedCurrenciesQuery {
    currentUser {
      slug
      ...useAcceptedCurrencies_publicUserInfoInterface
    }
  }
  ${useAcceptedCurrencies.fragments.publicUserInfoInterface}
` as TypedDocumentNode<
  CurrentUserAcceptedCurrenciesQuery,
  CurrentUserAcceptedCurrenciesQueryVariables
>;

export const useCurrentUserAcceptedCurrencies = () => {
  const updateUserProfile = useUpdateUserProfile();
  const { data } = useQuery(CURRENT_USER_ACCEPTED_CURRENCIES_QUERY);
  const { acceptedCurrencies, storedAcceptedCurrencies } =
    useAcceptedCurrencies({
      user: data?.currentUser,
    });

  const updateAcceptedCurrencies = useCallback(
    async (value: AcceptedCurrenciesValue) => {
      return updateUserProfile({
        marketplacePreferences: [
          {
            sports: [Sport.BASEBALL, Sport.FOOTBALL, Sport.NBA],
            name: MARKETPLACE_PREFERENCES_ACCEPTED_CURRENCIES,
            value,
          },
        ],
      });
    },
    [updateUserProfile]
  );

  return {
    acceptedCurrencies,
    updateAcceptedCurrencies,
    acceptedCurrenciesHaveBeenSet: !!storedAcceptedCurrencies,
  };
};
